/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2021 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { inject, observer } from 'mobx-react';
import axios from 'axios';
import PropTypes from 'prop-types';
import Framework, { shapes } from '@greenville/framework';
import {
  Typography,
  withStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper
} from '@material-ui/core';
import status from '../../../common/config/BulkIngestionConfig';
// import TooltipCustom from '../../../common/components/TooltipCustom';
// import utils from '../../../../common/utils';
import env from '../../../common/env';
import * as constants from '../../../common/constants';

const styles = (theme) => ({
  root: {
    width: '100%',
    bottom: theme.spacing(2)
  },
  metaRoot: {
    '& .MuiTableCell-root': {
      padding: theme.spacing(0.5),
      textAlign: 'center'
    }
  },
  tableText: {
    textAlign: 'center',
    color: '#6a7070',
    fontSize: 14
  }
});

const BulkIngestionTable = ({ classes, language, data }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const setStatusValue = (processStatus) => status.status.filter((y) => y.id === processStatus).map((x) => x.value);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleDownload = async (contentPath) => {
    let signedUrl = '';
    const path = contentPath.split('/').slice(1).join('/');
    const param = {
      contentPath: path,
      type: 'download'
    };
    const response = await axios.post(`${env.EVERGREEN_API_BASE_URL}${constants.CM_GET_SIGNED_URL}`, param);
    if (response && response.data && response.data.data) {
      signedUrl = response.data.data;
      const aTag = document.createElement('a');
      aTag.setAttribute('download', '');
      aTag.setAttribute('href', signedUrl);
      aTag.click();
    } else {
      const notification = {
        open: true,
        message: `Error downloading. Please try again !`,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    }
  };

  const getFileName = (path) => {
    const fileName = path.split('/').pop();
    return fileName.split('.').slice(0, -1).join('.');
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  return (
    <>
      <TableContainer className={classes.root} component={Paper}>
        <Table className={classes.metaRoot} stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('bulkutility.UUID')} />
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('bulkutility.CONTENT_PATH')} />
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('bulkutility.TOTAL_RECORDS')} />
                </Typography>
              </TableCell>
              {/* <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('bulkutility.FAILED_RECORDS')} />
                </Typography>
              </TableCell> */}
              <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('label.ACTION_USER')} />
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('bulkutility.UPLOADED_DATE')} />
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('label.STATUS')} />
                </Typography>
              </TableCell>
              {/* <TableCell align="center">
                <Typography variant="h5">
                  <FormattedMessage {...language.getText('common.CANCEL_PROCESS')} />
                </Typography>
              </TableCell> */}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length > 0 &&
              data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow>
                  <TableCell>
                    <Typography className={classes.tableText} component="div">
                      {row.bulk_ingestion_uuid}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      className={classes.tableText}
                      style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0000EE' }}
                      component="div"
                      onClick={() => handleDownload(row.content_path)}
                    >
                      {getFileName(row.content_path)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.tableText} component="div">
                      {row.total_records}
                    </Typography>
                  </TableCell>
                  {/* <TableCell>
                    <Typography className={classes.tableText} component="div">
                      {row.failed_records}
                    </Typography>
                  </TableCell> */}
                  <TableCell>
                    <Typography className={classes.tableText} component="div">
                      {row.action_user}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.tableText} component="div">
                      {row.uploaded_date}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography className={classes.tableText} component="div">
                      {setStatusValue(row.status)}
                    </Typography>
                  </TableCell>
                  {/* <TableCell>
                  <Button variant="outlined" color="primary" size="small" onClick={() => handleCancel(row)}>
                    <FormattedMessage {...language.getText('common.CANCEL')} />
                  </Button>
                </TableCell> */}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 30, 60, 100]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
      />
    </>
  );
};

BulkIngestionTable.propTypes = {
  classes: PropTypes.object.isRequired,
  language: shapes.language.isRequired,
  data: PropTypes.object.isRequired
};

export default withStyles(styles)(observer(inject('language')(BulkIngestionTable)));
